import { Capacitor } from '@capacitor/core';

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_BACKEND_ORIGIN = 'https://api.smartinventory.fr';

const isLocalBackend = (url) => {
  if (!url) return true;
  return /localhost|127\.0\.0\.1/i.test(String(url));
};

const isNativeCapacitorRuntime = () => {
  if (typeof window === 'undefined') return false;
  if (window.location?.protocol === 'capacitor:' || window.location?.protocol === 'ionic:') return true;
  if (typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform()) return true;
  if (typeof Capacitor?.getPlatform === 'function' && Capacitor.getPlatform() !== 'web') return true;
  const cap = window.Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

const resolvedBackendOrigin = (() => {
  if (configuredBackendUrl && !isLocalBackend(configuredBackendUrl)) {
    return configuredBackendUrl.replace(/\/+$/, '');
  }
  if (isNativeCapacitorRuntime()) {
    return DEFAULT_BACKEND_ORIGIN;
  }
  return window.location.origin;
})();

const CLIENT_ERROR_ENDPOINT = `${resolvedBackendOrigin}/api/client-errors`;
const SESSION_KEY = 'smartinventory_runtime_session_id';
const DEDUPE_WINDOW_MS = 10000;

let contextGetter = () => ({});
let listenersInstalled = false;
const recentEvents = new Map();

const getSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      return existing;
    }
    const generated = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, generated);
    return generated;
  } catch {
    return 'session-unavailable';
  }
};

const sanitizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(metadata).map(([key, value]) => {
      if (value == null) {
        return [key, value];
      }
      if (typeof value === 'object') {
        try {
          return [key, JSON.parse(JSON.stringify(value))];
        } catch {
          return [key, String(value)];
        }
      }
      return [key, String(value).slice(0, 4000)];
    })
  );
};

const shouldSkipEvent = (fingerprint) => {
  const now = Date.now();
  const lastSeen = recentEvents.get(fingerprint);
  recentEvents.set(fingerprint, now);
  for (const [key, timestamp] of recentEvents.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentEvents.delete(key);
    }
  }
  return lastSeen && now - lastSeen < DEDUPE_WINDOW_MS;
};

const postClientError = (payload) => {
  const serialized = JSON.stringify(payload);

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([serialized], { type: 'application/json' });
      const queued = navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, blob);
      if (queued) {
        return Promise.resolve();
      }
    }
  } catch {
    // Fall through to fetch
  }

  return fetch(CLIENT_ERROR_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
    },
    body: serialized,
    keepalive: true,
  }).catch(() => undefined);
};

export const reportRuntimeError = (error, metadata = {}) => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const message = error?.message || metadata.message || 'Unknown runtime error';
  const stack = error?.stack || metadata.stack || null;
  const fingerprint = `${metadata.kind || 'runtime'}:${message}:${stack || ''}`;
  if (shouldSkipEvent(fingerprint)) {
    return Promise.resolve();
  }

  const payload = {
    kind: metadata.kind || 'runtime.error',
    message,
    stack,
    component_stack: metadata.componentStack || null,
    url: window.location.href,
    pathname: window.location.pathname,
    user_agent: navigator.userAgent,
    session_id: getSessionId(),
    environment: process.env.NODE_ENV,
    build_version: process.env.REACT_APP_BUILD_VERSION || null,
    occurred_at: new Date().toISOString(),
    metadata: sanitizeMetadata({
      ...contextGetter(),
      ...metadata,
    }),
  };

  return postClientError(payload);
};

export const configureRuntimeLogger = (getContext) => {
  contextGetter = typeof getContext === 'function' ? getContext : () => ({});

  if (listenersInstalled) {
    return;
  }

  window.addEventListener('error', (event) => {
    reportRuntimeError(event.error || new Error(event.message || 'Window error'), {
      kind: 'window.error',
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const derivedError = reason instanceof Error
      ? reason
      : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

    reportRuntimeError(derivedError, {
      kind: 'window.unhandledrejection',
      rejection: typeof reason === 'string' ? reason : undefined,
    });
  });

  listenersInstalled = true;
};